/* istanbul ignore file */
import * as React from "react";

declare global {
  interface Window {
    dataLayer: object[];
    hj: () => void;
  }
}

const GTMContainerId = "GTM-TLVN598";
const GAScriptID = "GAScript";

export const GAContext = React.createContext<GAProps>({
  triggerEvent: (_) => _,
});

interface GAProviderProps {
  id?: string;
  isConsumerPage?: boolean;
}

interface GAContextProviderProps {
  triggerEvent: (event: object) => void;
}

const GAContextProvider: React.FC<GAContextProviderProps> = (props) => {
  const { triggerEvent } = props;
  const value = React.useMemo(() => {
    return { triggerEvent };
  }, [triggerEvent]);

  return (
    <GAContext.Provider value={value}>{props.children}</GAContext.Provider>
  );
};

class GAProvider extends React.Component<GAProviderProps> {
  constructor(props: GAProviderProps) {
    super(props);
    renderGAScript(this.props.id || GTMContainerId, props.isConsumerPage);
  }

  render() {
    return (
      <GAContextProvider triggerEvent={this.triggerEvent}>
        {this.props.children}
      </GAContextProvider>
    );
  }
  private triggerEvent = (event: object) => {
    if (window) {
      window.dataLayer.push(event);
    }
  };
}

const renderGAScript = (gtmId: string, isConsumerPage?: boolean) => {
  const GAScript = document.getElementById(GAScriptID) as HTMLScriptElement;
  if (GAScript) {
    if (GAScript.src.indexOf(gtmId) > -1) {
      return;
    } else if (GAScript.parentNode) {
      GAScript.parentNode.removeChild(GAScript);
    }
  }
  if (!window.hj) {
    window.hj = () => {
      // mock `hj` function to eliminate the `hj is not undefined` error in GTM
    };
  }

  window.dataLayer = window.dataLayer || [];
  const gaLayer = window.dataLayer.push.bind(window.dataLayer);
  window.dataLayer.push = (param: any) => {
    if (param && param.event) {
      return gaLayer(param);
    }
    return 0;
  };

  if (isConsumerPage === true) {
    window.dataLayer.push({
      event: "pageview",
      source: "UM",
      target: "",
      user: {},
    });
  }
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });
  const firstScriptTag = document.getElementsByTagName("script")[0],
    newScriptTag = document.createElement("script");
  newScriptTag.async = true;
  newScriptTag.src = "https://www.googletagmanager.com/gtm.js?id=" + gtmId;
  newScriptTag.id = GAScriptID;
  firstScriptTag.parentNode!.insertBefore(newScriptTag, firstScriptTag);
};

interface GAProps {
  triggerEvent: (event: object) => void;
}

function withGA<P extends GAProps = GAProps>(
  WrappedComponent: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof GAProps>> {
  return (props) => (
    <GAContext.Consumer>
      {(value) => (
        <WrappedComponent {...(props as P)} triggerEvent={value.triggerEvent} />
      )}
    </GAContext.Consumer>
  );
}

function useGA() {
  return React.useContext(GAContext);
}

export { GAProvider, GAProps, withGA, useGA };
